import { generateUUID } from '@/lib/utils';
import {
  decodeWorkingContext,
  encodeWorkingContext,
  type WorkingContext,
} from '@/lib/chat/working-context';

/**
 * A one-time handoff from an "Ask" launcher to a new conversation.
 *
 * `useHandoff`/`storeWorkingContext` (lib/context/use-handoff.ts,
 * lib/context/actions.ts) is the durable path: it round-trips through the
 * workspace so a link survives a new tab, a refresh, or being pasted to a
 * colleague. This module is the lighter sibling for the common case — Ask
 * opened from the current page, in the current tab — where that round trip
 * buys nothing but latency.
 *
 * Every stash mints its own key (`sixdegree.chat-prefill.<uuid>`), so two
 * concurrent launches — two tabs, a double click, two Ask buttons on the same
 * page — each get their own slot instead of racing on one fixed key. `Chat`
 * (components/chat.tsx) reads it back exactly once, keyed by the
 * `context_key` query parameter, and removes it immediately so a stale value
 * can never resurface on a later visit.
 */

const PREFILL_PREFIX = 'sixdegree.chat-prefill.';

function sessionStore(): Storage | null {
  try {
    return typeof sessionStorage !== 'undefined' ? sessionStorage : null;
  } catch {
    // Some environments (locked-down iframes, certain private-mode configs)
    // throw on access rather than returning undefined.
    return null;
  }
}

/**
 * Stash a context for the next conversation to pick up and return the
 * `context_key` value the launcher should append to the destination URL
 * (e.g. `/?context_key=<key>` or `/chat/<id>?context_key=<key>`).
 *
 * Storage failure (quota, private mode) is not fatal: the caller still gets a
 * key and can navigate. Arriving without context is a worse experience, not a
 * broken one.
 */
export function stashAskContext(context: WorkingContext): string {
  const key = generateUUID();
  const store = sessionStore();
  if (store) {
    try {
      store.setItem(`${PREFILL_PREFIX}${key}`, encodeWorkingContext(context));
    } catch {
      // The in-memory context is lost, but navigation still proceeds.
    }
  }
  return key;
}

/**
 * Consume a stashed context. Reads and removes it in the same call — a
 * `context_key` is single-use, so a page reload or a second mount of `Chat`
 * must not resurrect it.
 *
 * Returns `null` for a missing key, a key from a session that already
 * consumed it, or storage that is unavailable — all three must look
 * identical to the caller.
 */
export function consumeAskContext(
  key: string | null | undefined,
): WorkingContext | null {
  if (!key) return null;
  const store = sessionStore();
  if (!store) return null;
  const storageKey = `${PREFILL_PREFIX}${key}`;
  try {
    const raw = store.getItem(storageKey);
    if (!raw) return null;
    store.removeItem(storageKey);
    return decodeWorkingContext(raw);
  } catch {
    return null;
  }
}
